import _ from 'lodash';

import {AttachmentModel} from '../../../libs/shared/core/src/models/api/attachmentModel';
import {
  ChannelComposeModesEnum,
  ChannelExternalTypesEnum,
} from '../../../libs/shared/core/src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../libs/shared/core/src/models/api/channelCommonTypes';
import {
  channelErrorLevelPriority,
  ChannelErrorLevelsEnum,
  ChannelErrorModel,
} from '../../../libs/shared/core/src/models/api/channelErrorModel';
import {ChannelCustomModel, ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {ChannelProvidersEnum} from '../../../libs/shared/core/src/models/api/channelPreferencesModel';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {InboxModel} from '../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {ApplicationChannelTypeModel} from '../../models/api/applicationChannelTypeModel';
import {ExternalChannelTypeModel} from '../../models/api/externalChannelTypeModel';
import {isNewChannel, NewChannelModel} from '../../models/api/newChannelModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {SettingsChannelEditTabsEnum} from '../../models/url/models/channelsUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../models/url/models/types';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {teammateCanAccessInbox} from '../modelHelpers';

/** Channel Resource error_type  */
export enum ChannelErrorsEnum {
  MISSING_O365_SEND_PERMISSIONS = 'missing_o365_send_permissions',
}

/*
 * Helpers.
 */

export function isTeamChannel(channel: ChannelModel) {
  return isTeamNamespace(channel.namespace);
}

export function isTeammateChannel(channel: ChannelModel) {
  return isTeammateNamespace(channel.namespace);
}

export function isTeammateParticipatingChannel(
  teammate: TeammateModel,
  inbox: InboxModel | undefined,
  channel: ChannelModel,
) {
  if (!inbox || inbox.id !== channel.inboxId) {
    return false;
  }

  if (!teammateCanAccessInbox(teammate, inbox)) {
    return false;
  }

  return !isTeammateNamespace(inbox.namespace) || inbox.namespace === teammate.namespace;
}

interface FilterComposeChannelsOptions {
  recipients?: ReadonlyArray<VisualRecipientModel>;
  specifiedChannelId?: number;
  specifiedMessageType?: MessageTypesEnum;
}

export function filterComposeChannels(
  participatingChannels: ReadonlyArray<ChannelModel>,
  options: FilterComposeChannelsOptions = {},
): ReadonlyArray<ChannelModel> {
  const {recipients, specifiedChannelId, specifiedMessageType} = options;

  // Filter channels that can be used to compose.
  return participatingChannels.filter((channel) => {
    if (channel.behaviors.composeMode === ChannelComposeModesEnum.UNSUPPORTED || channel.isSandbox) {
      return false;
    }

    // If recipients were passed, filter channels that can actually reach them.
    if (recipients && !recipients.every((r) => canSendToRecipient(channel, r))) {
      return false;
    }

    // If a specified channel ID was passed, we should only accept this one.
    if (specifiedChannelId && specifiedChannelId !== channel.id) {
      return false;
    }

    // If a specified messageType was passed, we should only accept this one.
    if (specifiedMessageType && specifiedMessageType !== channel.messageType) {
      return false;
    }

    return true;
  });
}

export function buildPatchedChannelsForInbox(channels: ReadonlyArray<ChannelModel>, inbox: InboxModel) {
  return (
    _(channels)
      // Only update channels belonging to this inbox.
      .filter((c) => c.inboxId === inbox.id)
      // Only update channels that haven't been updated before this inbox.
      .filter((c) => c.updatedAt < inbox.updatedAt)
      // Only update channels that have the incorrect namespace.
      .filter((c) => c.namespace !== inbox.namespace)
      .map((channel) => ({
        ...channel,
        namespace: inbox.namespace,
      }))
      .value()
  );
}

export function buildChannelSettingsPath(channel: ChannelModel) {
  return buildSettingsPath({
    rootPage: SettingsRootPagesEnum.CHANNELS,
    namespace: channel.namespace,
    resourcePage: SettingsResourcePagesEnum.EDIT,
    channelId: channel.id,
    tab: SettingsChannelEditTabsEnum.SETTINGS,
  });
}

export function getChannelMessageLengthLimit(channel: ChannelModel) {
  return channel.behaviors.messageLengthLimit;
}

export function getChannelAttachmentsMaxSize(channel: ChannelModel) {
  const {attachments} = channel.behaviors;
  return attachments && attachments.maxTotalSize;
}

export function getChannelAttachmentsMaxCount(channel: ChannelModel) {
  const {attachments} = channel.behaviors;
  return attachments && attachments.maxCount;
}

export function getChannelRecipientsCountLimit(channel: ChannelModel) {
  return channel.preferences.recipientsCountLimit;
}

export function isChannelAffectedByRecipientLimit(channel: ChannelModel) {
  return ![ChannelTypesEnum.YALO_WHATSAPP, ChannelTypesEnum.TWITTER_DM].includes(channel.type);
}

export function isChannelSupportedAttachment(channel: ChannelModel, attachment: AttachmentModel) {
  const {attachments} = channel.behaviors;
  // We lower case all the types when comparing so that it's not case sensitive
  return (
    attachments &&
    (!attachments.types ||
      attachments.types.some((type) => type.toLowerCase() === attachment.contentType.toLowerCase()))
  );
}

export function doesChannelSupportSignatures(channel: ChannelModel) {
  return channel.messageType === MessageTypesEnum.EMAIL;
}

export function channelHasError(channel: ChannelModel) {
  return Boolean(channel.error);
}

export function findHighestPriorityChannelError(channels: ReadonlyArray<ChannelModel>) {
  return channels.reduce((selected: ChannelErrorModel | undefined, {error}) => {
    if (!error) {
      return selected;
    }

    if (
      !selected ||
      channelErrorLevelPriority.indexOf(error.level) < channelErrorLevelPriority.indexOf(selected.level)
    ) {
      return error;
    }

    return selected;
  }, undefined);
}

export function isActionableError(error: ChannelErrorModel) {
  return error.level === ChannelErrorLevelsEnum.ERROR;
}

export function canSendToRecipient(channel: ChannelModel, recipient: VisualRecipientModel) {
  return channel.behaviors.supportedContactSources.includes(recipient.contact.source);
}

export function isChannelValidated(channel: ChannelModel) {
  return channel.preferences.canSend;
}

export function isReplyAllowedWithoutInboxAccess(channel: ChannelModel) {
  return channel.preferences.canReplyWithoutInboxAccess;
}

export function channelSupportsCalendarSync(channel: ChannelModel) {
  return [ChannelTypesEnum.GMAIL, ChannelTypesEnum.OFFICE365].includes(channel.type);
}

export function channelHasDnsSettings(channel: ChannelModel) {
  return (
    channel.type === ChannelTypesEnum.SMTP && channel.preferences.provider === ChannelProvidersEnum.SENDGRID
  );
}

export function isDemoChannel(channel: ChannelModel) {
  return channel.preferences.isDemo;
}

export function channelNeedsDeliverabilitySetting(channel: ChannelModel) {
  return isTeamChannel(channel) && !isDemoChannel(channel) && channelHasDnsSettings(channel);
}

export function mapChannelToChannelType(channel: ChannelModel): ChannelTypesEnum {
  if (channel.type === ChannelTypesEnum.GMAIL || channel.type === ChannelTypesEnum.OFFICE365) {
    return ChannelTypesEnum.SMTP;
  }

  return channel.type;
}

/*
 * Ported from `front` with minor changes.
 */

export function findUniqueChannelType(
  channel: ChannelModel | NewChannelModel,
  externalChannelTypes: ResourceMap<ExternalChannelTypeModel>,
) {
  if (isNewChannel(channel)) {
    return findUniqueChannelTypeForNewChannel(channel, externalChannelTypes);
  }

  return findUniqueChannelTypeForChannel(channel, externalChannelTypes);
}

export function findApplicationChannelType(
  channel: ChannelModel | NewChannelModel | undefined,
  applicationChannelTypes: ResourceMap<ApplicationChannelTypeModel>,
) {
  if (isNewChannel(channel)) {
    return (
      (hasExternalChannelTypeId(channel.settings) &&
        applicationChannelTypes[channel.settings.externalChannelTypeId]) ||
      undefined
    );
  }

  return (
    (channel?.applicationChannelTypeUid && applicationChannelTypes[channel.applicationChannelTypeUid]) ||
    undefined
  );
}

export function findExternalChannelType(
  channel: ChannelModel | NewChannelModel | undefined,
  externalChannelTypes: ResourceMap<ExternalChannelTypeModel>,
) {
  if (isNewChannel(channel)) {
    if (!hasExternalChannelTypeId(channel.settings)) {
      return undefined;
    }
    return externalChannelTypes[channel.settings.externalChannelTypeId];
  }

  if (!channel || !channel.externalChannelTypeId) {
    return undefined;
  }

  return externalChannelTypes[channel.externalChannelTypeId];
}

function findUniqueChannelTypeForChannel(
  channel: ChannelModel,
  externalChannelTypes: ResourceMap<ExternalChannelTypeModel>,
) {
  if (channel.type !== ChannelTypesEnum.CUSTOM) {
    return channel.type;
  }

  if (!channel.externalChannelTypeId) {
    return ChannelTypesEnum.CUSTOM;
  }

  const externalChannelType = externalChannelTypes[channel.externalChannelTypeId];
  if (!externalChannelType || !externalChannelType.name) {
    return ChannelTypesEnum.CUSTOM;
  }

  return getExternalChannelTypeFromName(externalChannelType.name);
}

function findUniqueChannelTypeForNewChannel(
  channel: NewChannelModel,
  externalChannelTypes: ResourceMap<ExternalChannelTypeModel>,
) {
  if (channel.type !== ChannelTypesEnum.CUSTOM) {
    return channel.type;
  }

  if (!hasExternalChannelTypeId(channel.settings)) {
    return ChannelTypesEnum.CUSTOM;
  }

  const externalChannelType = externalChannelTypes[channel.settings.externalChannelTypeId];
  if (!externalChannelType || !externalChannelType.name) {
    return ChannelTypesEnum.CUSTOM;
  }

  return getExternalChannelTypeFromName(externalChannelType.name);
}

function hasExternalChannelTypeId(settings: unknown): settings is {externalChannelTypeId: number} {
  return (
    typeof settings === 'object' &&
    settings !== null &&
    'externalChannelTypeId' in settings &&
    typeof settings.externalChannelTypeId === 'number'
  );
}

/**
 * Attempt to retrieve a more precise type than custom from external channel name.
 *
 * This method also helps to determine the channel icon used, given
 * the external channel type's name
 */
export const getExternalChannelTypeFromName = (
  externalChannelTypeName: string,
): ChannelExternalTypesEnum | ChannelTypesEnum.CUSTOM => {
  if (!externalChannelTypeName) {
    return ChannelTypesEnum.CUSTOM;
  }

  const channelNameLowerCased = externalChannelTypeName.toLowerCase();
  if (channelNameLowerCased.includes('drift')) {
    return ChannelExternalTypesEnum.DRIFT;
  }

  if (channelNameLowerCased.includes('dialpad sms')) {
    return ChannelExternalTypesEnum.DIALPAD_SMS;
  }

  if (channelNameLowerCased.includes('dialpad')) {
    return ChannelExternalTypesEnum.DIALPAD;
  }

  if (channelNameLowerCased.includes('telegram')) {
    return ChannelExternalTypesEnum.TELEGRAM;
  }

  if (channelNameLowerCased.includes('justcall')) {
    return ChannelExternalTypesEnum.JUSTCALL;
  }

  if (channelNameLowerCased.includes('yalo')) {
    return ChannelExternalTypesEnum.YALO;
  }

  if (channelNameLowerCased.includes('liveweb')) {
    return ChannelExternalTypesEnum.LIVEWEB;
  }

  if (channelNameLowerCased.includes('meya')) {
    return ChannelExternalTypesEnum.MEYA;
  }

  if (channelNameLowerCased.includes('instagram')) {
    return ChannelExternalTypesEnum.INSTAGRAM;
  }

  if (channelNameLowerCased.includes('sendbee / whatsapp')) {
    return ChannelExternalTypesEnum.SENDBEE_WHATSAPP;
  }

  if (channelNameLowerCased.includes('slack')) {
    return ChannelExternalTypesEnum.SLACK;
  }

  if (channelNameLowerCased.includes('facebook')) {
    return ChannelExternalTypesEnum.FACEBOOK;
  }

  return ChannelTypesEnum.CUSTOM;
};

export function isChannelDenied(channel: ChannelModel) {
  if (!channel.error?.type) {
    return false;
  }
  return [
    'denied',
    'no_read_page_mailboxes_perm',
    'app_perm_revoked',
    'no_manage_pages_perm',
    'facebook_perm',
  ].includes(channel.error.type);
}

export function checkCustomChannelVersion(channel: ChannelCustomModel, version: number): boolean {
  const parsedVersionNumber = Number(channel.preferences.version.replace(/\D/g, ''));

  return parsedVersionNumber === version;
}

export function isExternalChannel(channel: ChannelModel): boolean {
  return Boolean(channel.externalChannelTypeId);
}

export function isExistingChannelAddress(
  channels: ReadonlyArray<ChannelModel>,
  address: string,
  messageType?: MessageTypesEnum,
) {
  const addressLc = address.toLowerCase();

  return channels.some(
    (channel) =>
      (!messageType || channel.messageType === messageType) &&
      ((channel.sendAddress && channel.sendAddress.toLowerCase() === addressLc) ||
        (channel.address && channel.address.toLowerCase() === addressLc)),
  );
}
